import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, EventEmitter, input, Output } from '@angular/core';
import { LocationItem } from '../../models/location-item.model';
import { SelectionType } from '../../models/selection-type.enum';

export interface HierarchySelectionEvent {
  item: LocationItem;
  type: SelectionType;
}

/**
 * Recursive tree-row component.
 * Renders a flat list of <li> elements; each <li> may contain a nested
 * <f-location-hierarchy-item> for its children (rendered lazily on first expand).
 */
@Component({
  selector: 'f-location-hierarchy-item',
  templateUrl: './location-hierarchy-item.component.html',
  standalone: true,
  imports: [CommonModule]
})
export class FireflyLocationHierarchyItemComponent {
  readonly items = input<LocationItem[]>([]);
  readonly level = input<number>(0);
  readonly excludeText = input<string>('Exclude');
  readonly excludedText = input<string>('Excluded');
  readonly includeText = input<string>('Include');
  readonly includedText = input<string>('Included');

  readonly selectedItems = input<LocationItem[]>([]);
  private readonly _selectionMap = computed(
    () => new Map(this.selectedItems().map(s => [s.key, s.data.selectionType]))
  );

  /** Emitted when the user clicks +/- on a location row */
  @Output() itemSelect = new EventEmitter<HierarchySelectionEvent>();
  /** Emitted when the user clicks the row itself (expand/collapse or city-select) */
  @Output() itemToggle = new EventEmitter<LocationItem>();

  readonly SelectionType = SelectionType;

  constructor(private cdr: ChangeDetectorRef) {}

  toggle(item: LocationItem, event: Event): void {
    event.stopPropagation();
    if (item.children) {
      item.isCollapsed = !item.isCollapsed;
    }
    this.cdr.markForCheck();
    this.itemToggle.emit(item);
  }

  onSelect(item: LocationItem, type: SelectionType, event: Event): void {
    event.stopPropagation();
    this.itemSelect.emit({ item, type });
  }

  /** Bubble child events up to the root component */
  onChildSelect(event: HierarchySelectionEvent): void {
    this.itemSelect.emit(event);
  }

  onChildToggle(item: LocationItem): void {
    this.itemToggle.emit(item);
  }

  // ─── State helpers used by the template ──────────────────────────────────

  isActive(item: LocationItem): boolean {
    let current: LocationItem | null = item;
    while (current) {
      if (current.data.selectionType !== SelectionType.None) return true;
      current = current.parent;
    }
    return false;
  }

  isIncluded(item: LocationItem): boolean {
    return this.effectiveType(item) === SelectionType.Include;
  }

  isExcluded(item: LocationItem): boolean {
    return this.effectiveType(item) === SelectionType.Exclude;
  }

  /**
   * Returns the effective SelectionType for a row by walking UP the parent
   * chain — an ancestor's explicit selection makes child rows appear
   * included/excluded (inherited selection).
   *
   * Uses the reactive `_selectionMap` (built from the `selectedItems` signal
   * input) so that both freshly-selected and pre-loaded items are reflected
   * correctly without requiring an explicit `detectChanges()`.
   */
  private effectiveType(item: LocationItem): SelectionType {
    const map = this._selectionMap();
    let current: LocationItem | null = item;
    while (current) {
      const type = map.get(current.key);
      if (type !== undefined && type !== SelectionType.None) return type;
      current = current.parent;
    }
    return SelectionType.None;
  }

  trackByKey(_: number, item: LocationItem): string {
    return item.key;
  }
}
